"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect, useState, useSyncExternalStore } from "react";

export const META_PIXEL_ID = "873779399131282";
const META_TRACKING_PARAMETER = "meta_tracking";
const META_TRACKING_STORAGE_KEY = "impulsa:meta-tracking-disabled:v1";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: Window["fbq"];
  }
}

export function MetaPixel() {
  const pathname = usePathname();
  const isPixelEnabled = useSyncExternalStore<boolean | null>(
    subscribeToTrackingPreference,
    getPixelEnabledSnapshot,
    getServerPixelEnabledSnapshot,
  );
  const [isPixelReady, setIsPixelReady] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const trackingPreference = url.searchParams.get(META_TRACKING_PARAMETER);

    if (trackingPreference === "off") {
      setTrackingDisabled(true);
    } else if (trackingPreference === "on") {
      setTrackingDisabled(false);
    }

    if (trackingPreference === "off" || trackingPreference === "on") {
      url.searchParams.delete(META_TRACKING_PARAMETER);
      window.history.replaceState(window.history.state, "", url);
    }

  }, []);

  useEffect(() => {
    if (isPixelEnabled && isPixelReady) {
      window.fbq?.("track", "PageView");
    }
  }, [isPixelEnabled, isPixelReady, pathname]);

  if (isPixelEnabled === null) {
    return null;
  }

  if (!isPixelEnabled) {
    return <MetaTrackingDisabledIndicator />;
  }

  return (
    <>
      <Script id="meta-pixel-bootstrap" strategy="afterInteractive">
        {metaPixelBootstrap}
      </Script>
      <Script
        id="meta-pixel-library"
        onReady={() => setIsPixelReady(true)}
        src="https://connect.facebook.net/en_US/fbevents.js"
        strategy="afterInteractive"
      />
    </>
  );
}

function MetaTrackingDisabledIndicator() {
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-3 top-3 z-50 rounded-full border border-amber-300 bg-amber-50/95 px-3 py-1.5 text-xs font-bold text-amber-900 shadow-sm backdrop-blur"
      role="status"
    >
      Meta desactivado
    </div>
  );
}

function isTrackingDisabled(): boolean {
  try {
    return window.localStorage.getItem(META_TRACKING_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function getPixelEnabledSnapshot(): boolean {
  const trackingPreference = new URL(window.location.href).searchParams.get(
    META_TRACKING_PARAMETER,
  );

  if (trackingPreference === "off") {
    return false;
  }

  if (trackingPreference === "on") {
    return true;
  }

  return !isTrackingDisabled();
}

function getServerPixelEnabledSnapshot(): null {
  return null;
}

function subscribeToTrackingPreference(): () => void {
  return () => undefined;
}

function setTrackingDisabled(isDisabled: boolean): void {
  try {
    if (isDisabled) {
      window.localStorage.setItem(META_TRACKING_STORAGE_KEY, "true");
    } else {
      window.localStorage.removeItem(META_TRACKING_STORAGE_KEY);
    }
  } catch {
    // The current page still respects the URL preference when storage is unavailable.
  }
}

const metaPixelBootstrap = `
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;
    n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '${META_PIXEL_ID}');
`;
